import React, { FC, useEffect, useState } from "react";

import {
  Button,
  Filters,
  getFiltersOrderFromLocalStorage,
  ILibFilters,
  Refresh,
  Spinner,
} from "@infologistics/frontend-libraries";

import { IEnvelopesHeadProps as IProps } from "@views/envelopes/types";
import { useAppDispatch, useAppSelector } from "@app/hooks/redux";
import {
  applyExtendedDateStringFilterParams,
  fillFilters,
  fillRenderValue,
  getFiltersTranslations,
  getParamsFromFilters,
} from "@app/utils/filters";
import {
  displayErrorNotification,
  getInstance,
  getOldInstance,
  getParamsFromSearch,
} from "@app/utils/utils";
import { useLocation, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import queryString from "query-string";
import { IFiltersResult } from "@app/utils/typesFilter";
import { AxiosError } from "axios";
import { closeLoader } from "@app/store/modules/loader";
import { envelopesFilters } from "./envelopesFilters";
import { QueryLikeFilter } from "@app/common/QueryLikeFilter";
import { setSearchContext } from "@app/store/modules/utils";
import { FilterName } from "@app/const/filters";
import EnvelopesService from '@services/envelopes';
import { DateTime } from "luxon";
import { DateFormat, Languages, needResetParams, ResponseCode } from "@app/const/consts";
import { useSearchParams } from "react-router-dom";
import { IParams } from "@app/types";
import cn from 'classnames';

import styles from './styles.module.scss';

const LS_ENVELOPES_FILTERS_KEY = "envelopes";

const EnvelopesHead: FC<IProps> = ({ loadEnvelopes }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(["envelopes", "filters", "common", "envelope"]);
  const { instance } = useAppSelector((state) => state.utils);
  const userOguid = useAppSelector((state) => state.user.profile.oguid);
  const appState = useAppSelector((state) => state);
  const { boxes } = useAppSelector((state) => state.boxes);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { directionOrFolder, boxOguid, directionParam } = useParams();
  const [ searchParams ] = useSearchParams();

  const [isOpenFilters, setIsOpenFilters] = useState(false);
  const [filtersOrder, setFiltersOrder] = useState<string[]>(
    envelopesFilters.order
  );
  const [filters, setFilters] = useState<ILibFilters>(envelopesFilters.data);
  const [queryLikeKey, setQueryLikeKey] = useState(false);
  const [blockSearchUpd, setBlockSearchUpd] = useState(false);
  const [blockRegistryDownload, setBlockRegistryDownload] = useState(false);

  const personalBoxOguid = useAppSelector((state) => state.user.profile.boxOguid);
  const currentBoxOguid = boxOguid ?? personalBoxOguid;

  useEffect(() => {
    updateFiltersInState().catch(handleCatchError);

    return () => {
      dispatch(setSearchContext([]));
    };
  }, []);

  useEffect(() => {
    if (!blockSearchUpd) {
      updateFiltersInState(
        !location.search ? envelopesFilters.data : undefined
      ).catch(handleCatchError);
      setQueryLikeKey(!queryLikeKey);
    }
    setBlockSearchUpd(false);
    setIsOpenFilters(false);
  }, [location.search]);

  useEffect(() => {
    setIsOpenFilters(false);
  }, [directionOrFolder]);

  const changeFiltersState = (isOpenFilters: boolean): void =>
    setIsOpenFilters(isOpenFilters);

  const handleFilterButtonClick = async (): Promise<void> => {
    // await updateFiltersInState();

    changeFiltersState(!isOpenFilters);
  };

  const handleDownloadRegistryButtonClick = async (): Promise<void> => {
    const direction = directionOrFolder ?? directionParam;
    const otherSearchParams: IParams = applyExtendedDateStringFilterParams(
      searchParams,
      true
    );

    const params = {
      ...otherSearchParams,
      direction: direction?.toUpperCase(),
      ...(directionOrFolder ? { boxOguid: currentBoxOguid } : {}),
      ...needResetParams,
    };

    try {
      setBlockRegistryDownload(true);
      const resp = await EnvelopesService.getEnvelopesExcel(params);
      const { data, status } = resp;

      if (status === ResponseCode.GET) {
        const date = DateTime.now().toFormat(
          language === Languages.RU
            ? DateFormat.DATE_FULL_YEAR_DATEPICKER
            : DateFormat.FULL_DATE_DOTS
        );
        const filename = `${date}_${direction}_${
          directionOrFolder
            ? boxes.find((val) => val.oguid === currentBoxOguid)?.email
            : 'full'
        }.xlsx`;

        const link = document.createElement('a');
        link.href = URL.createObjectURL(data); // Create a URL for the Blob
        link.download = filename; // Set the desired filename for the download

        // Append the link to the document, click it, and then remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Revoke the Object URL to free up memory
        URL.revokeObjectURL(link.href);
      }
    } catch (err: any) {
      displayErrorNotification(err);
    }

    setBlockRegistryDownload(false);
  };

  const submitFilters = (filters: ILibFilters): void => {
    const paramsFromFilters = getParamsFromFilters(
      filters,
      LS_ENVELOPES_FILTERS_KEY
    );

    let params = queryString.stringify(paramsFromFilters);
    const paramsFromSearch = getParamsFromSearch(location.search);
    if (FilterName.QUERY_LIKE in paramsFromSearch) {
      params += `&${FilterName.QUERY_LIKE}=` + paramsFromSearch[FilterName.QUERY_LIKE];
    }
    setBlockSearchUpd(true);
    navigate(`${location.pathname}?${params}`, { replace: true });
    const newFilters = fillRenderValue({ data: filters, search: params, t });

    setFilters(newFilters);
  };

  const getFilters = async (
    filtersData?: ILibFilters
  ): Promise<IFiltersResult> => {
    // const {
    //   fields,
    //   location: { search },
    //   routeAdditionalProp,
    //   state,
    //   t
    // } = this.props
    const { search } = location;

    const myFilters = filtersData ?? filters;
    const data: ILibFilters = { ...myFilters };

    const initialFiltersFromStorage = getFiltersOrderFromLocalStorage(
      LS_ENVELOPES_FILTERS_KEY
    );

    // const isFilter = routeAdditionalProp === RouteName.FILTERS

    // let filtersSearch = search

    // if (!search && isFilter) {
    //   const userFilter = this.getUserFilter()
    //   if (userFilter) filtersSearch = userFilter.params
    // }

    const newFilters = await fillFilters({
      data,
      order: [...filtersOrder],
      // search: filtersSearch,
      search,
      t,
      state: appState,
      // fields,
      key: LS_ENVELOPES_FILTERS_KEY,
      // onSetBreak: this.handleSetBreak,
      user: userOguid,
    });

    const newFiltersFromStorage = getFiltersOrderFromLocalStorage(
      LS_ENVELOPES_FILTERS_KEY
    );

    const filtersFromStorage =
      initialFiltersFromStorage.length > newFiltersFromStorage.length
        ? initialFiltersFromStorage
        : newFiltersFromStorage;

    localStorage.setItem(
      LS_ENVELOPES_FILTERS_KEY,
      JSON.stringify(filtersFromStorage)
    );

    return {
      filters: newFilters,
      order: filtersFromStorage,
    };
  };

  const updateFiltersInState = async (
    filtersData?: ILibFilters
  ): Promise<void> => {
    const { filters, order } = await getFilters(filtersData);

    setFilters(filters);
    setFiltersOrder(order);
  };

  const handleCatchError = (err: AxiosError): void => {
    dispatch(closeLoader());
    // this.setState({ userFilterIsBreak: true })
    displayErrorNotification(err);
  };

  return (
    <>
      <div className='d-flex align-items-center mb-4'>
        <Refresh onClick={loadEnvelopes} title={t('common:refresh')} />
        <h1 className='ml-4 mb-0'>{t('envelopes:title')}</h1>

        <QueryLikeFilter
          className='ml-auto mr-4'
          key={queryLikeKey + ''}
          // userFilter={userFilter}
        />
        <Button onClick={handleFilterButtonClick} classes='mr-4'>
          {t('common:filters')}
        </Button>
        <div className='d-flex relative align-items-center justify-content-center'>
          <Button
            onClick={handleDownloadRegistryButtonClick}
            isDisabled={blockRegistryDownload}
          >
            {t('common:downloadRegistry')}
          </Button>
          {blockRegistryDownload && (
            <div className={cn(styles.spinner__div, 'fl-button_spinner')}>
              <Spinner />
            </div>
          )}
        </div>
      </div>
      <div className='d-flex align-items-center'>
        <Filters
          className='mb-4'
          filters={filters}
          language={language}
          submitFilters={submitFilters}
          translations={getFiltersTranslations(t)}
          isOpen={isOpenFilters}
          changeOpenStatus={changeFiltersState}
          order={filtersOrder}
          // options={getGroupedFieldOptions(this.getOptions(), fields, t)}
          // onAdd={this.handleAddFilter}
          // onDelete={this.handleDeleteFilter}
          // isSaved={this.isUserFilterSaved()}
          // onSave={this.handleOpenUserFiltersModal}
          // isHorizontalNavMode={getIsHorizontalNavMode()}
          // hasResult={hasFilter}
          instance={getOldInstance(getInstance(instance))}
        />
      </div>
    </>
  );
};

export default EnvelopesHead;
